import { Modal, StyleSheet, View } from 'react-native';
import { Button } from '../../../components/primitives/Button';
import { HStack, VStack } from '../../../components/primitives/Stack';
import { Body, Heading4 } from '../../../components/primitives/Text';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import { useProductLinkedLeads } from '../hooks';

interface DeleteProductDialogProps {
  organizationId: string;
  product: { id: string; name: string } | null;
  onConfirm: (productId: string) => void;
  onCancel: () => void;
}

export function DeleteProductDialog({
  organizationId,
  product,
  onConfirm,
  onCancel,
}: Readonly<DeleteProductDialogProps>) {
  const { t } = useTranslation();
  const linked = useProductLinkedLeads(organizationId, product?.id ?? null);

  if (!product) return null;

  const linkedLeads = linked.data ?? 0;
  const title = t('onboardingWizard.products.deleteDialog.title', { name: product.name });
  const linkedKey =
    linkedLeads === 1
      ? 'onboardingWizard.products.deleteDialog.linkedLeadsOne'
      : 'onboardingWizard.products.deleteDialog.linkedLeadsOther';

  return (
    <Modal visible transparent animationType="fade" onRequestClose={onCancel}>
      <View style={styles.overlay}>
        <View
          testID="delete-product-dialog"
          style={styles.card}
          accessibilityLabel={title}
          accessibilityViewIsModal
        >
          <Heading4 style={styles.title} accessibilityRole="header">
            {title}
          </Heading4>
          <VStack spacing="sm" style={styles.body}>
            {linkedLeads > 0 ? (
              <Body
                testID="delete-product-dialog-linked"
                style={styles.text}
                accessibilityLiveRegion="polite"
              >
                {t(linkedKey, { count: linkedLeads })}
              </Body>
            ) : null}
            {linked.isError ? (
              <Body
                testID="delete-product-dialog-linked-error"
                style={styles.text}
                accessibilityLiveRegion="polite"
              >
                {t('onboardingWizard.products.deleteDialog.linkedLeadsError')}
              </Body>
            ) : null}
          </VStack>
          <HStack spacing="md" style={styles.footer}>
            <Button
              testID="delete-product-dialog-cancel"
              variant="outline"
              size="sm"
              onPress={onCancel}
              accessibilityLabel={t('onboardingWizard.products.deleteDialog.cancel')}
            >
              {t('onboardingWizard.products.deleteDialog.cancel')}
            </Button>
            <Button
              testID="delete-product-dialog-confirm"
              variant="primary"
              size="sm"
              onPress={() => {
                if (!linked.isPending) onConfirm(product.id);
              }}
              disabled={linked.isPending}
              accessibilityState={{ disabled: linked.isPending }}
              style={styles.dangerButton}
              accessibilityLabel={t('onboardingWizard.products.deleteDialog.confirm')}
            >
              {t('onboardingWizard.products.deleteDialog.confirm')}
            </Button>
          </HStack>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: tokens.spacing.lg,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    backgroundColor: colors.background.elevated,
    borderRadius: tokens.radius.lg,
    borderWidth: 1,
    borderColor: colors.status.error.border,
    padding: tokens.spacing.lg,
  },
  title: {
    color: colors.text.primary,
  },
  body: {
    marginTop: tokens.spacing.md,
  },
  text: {
    color: colors.text.secondary,
    lineHeight: 22,
  },
  footer: {
    marginTop: tokens.spacing.lgPlus,
    justifyContent: 'flex-end',
  },
  dangerButton: {
    backgroundColor: colors.status.error.main,
    borderColor: colors.status.error.main,
  },
});
